// Scale test panel embedded below RegisteredScalesSection in the Scales tab.
// Lets supervisors select a scale, test connection, and view live weight readings.
// Reuses the existing useScaleWeight hook and renders the readout inline. (It previously
// referenced the ScaleWeight / ScaleConnectionStatus components, which were only ever used by
// the retired desktop-operator flow and were deleted in WCO-3111; this file never imported them.)

import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { Activity, Trash2, Wifi, WifiOff, RefreshCw } from 'lucide-react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { useScaleWeight } from '../../../hooks/useScaleWeight';
import productionService from '../../../services/productionService';

interface ScaleEntry {
  setting_key: string;
  config: {
    ip: string;
    port: number;
    nickname: string;
    station_id: number;
  };
}

interface LogEntry {
  ts: string;
  message: string;
  type: 'weight' | 'ack' | 'state' | 'error';
}

const ScaleTestPanel: React.FC = () => {
  const { t } = useLanguage();
  const [selectedKey, setSelectedKey] = useState('');
  const [selectedStationId, setSelectedStationId] = useState<number | undefined>();
  const [readMode, setReadMode] = useState<'continuous' | 'on_print'>('continuous');
  const [log, setLog] = useState<LogEntry[]>([]);
  const logRef = useRef<HTMLDivElement>(null);

  // Fetch registered scales
  const { data: scales = [] } = useQuery<ScaleEntry[]>(
    'registered-scales',
    async () => {
      const all = await productionService.getConfigSettings();
      return all
        .filter(s => s.setting_key.startsWith('scale_'))
        .map(s => {
          try {
            return { setting_key: s.setting_key, config: JSON.parse(s.setting_value) };
          } catch {
            return null;
          }
        })
        .filter(Boolean) as ScaleEntry[];
    }
  );

  // Connect to the selected scale's station via WebSocket
  const {
    weight,
    unit,
    status,
    isStable,
    error,
    connect,
    disconnect,
    tare,
    sendMessage,
  } = useScaleWeight({
    stationId: selectedStationId,
    autoConnect: false,
  });

  // Stable weight (S command) handler
  const [fetchingStable, setFetchingStable] = useState(false);
  const handleGetStableWeight = async () => {
    if (!selectedKey) return;
    setFetchingStable(true);
    addLog({ ts: new Date().toISOString(), message: 'Requesting stable weight (S command)...', type: 'state' });
    try {
      const res = await fetch(`/api/scale/scales/${selectedKey}/stable-weight`);
      const data = await res.json();
      if (data.success) {
        addLog({ ts: new Date().toISOString(), message: `Stable: ${data.weight} ${data.unit}`, type: 'weight' });
      } else {
        addLog({ ts: new Date().toISOString(), message: `S command failed: ${data.error}`, type: 'error' });
      }
    } catch {
      addLog({ ts: new Date().toISOString(), message: 'S command request failed', type: 'error' });
    }
    setFetchingStable(false);
  };

  // Reconnect handler
  const [reconnecting, setReconnecting] = useState(false);
  const handleReconnect = async () => {
    if (!selectedKey) return;
    setReconnecting(true);
    try {
      await fetch(`/api/scale/scales/${selectedKey}/reconnect`, { method: 'POST' });
    } catch { /* ignore */ }
    setReconnecting(false);
  };

  // Send setMode command when toggle changes
  const handleModeChange = (mode: 'continuous' | 'on_print') => {
    setReadMode(mode);
    if (selectedStationId) {
      sendMessage({ type: 'setMode', station_id: selectedStationId, mode });
    }
  };

  // When a scale is selected, connect to its station
  useEffect(() => {
    if (selectedKey) {
      const scale = scales.find(s => s.setting_key === selectedKey);
      if (scale) {
        setSelectedStationId(scale.config.station_id);
        setLog([]);
      }
    } else {
      setSelectedStationId(undefined);
      disconnect();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, scales]);

  // Connect when station ID changes
  useEffect(() => {
    if (selectedStationId) {
      connect();
    }
    return () => disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStationId]);

  // Log weight updates
  useEffect(() => {
    if (weight !== 0 || status === 'connected') {
      addLog({ ts: new Date().toISOString(), message: `${weight} ${unit} ${isStable ? '(stable)' : '(dynamic)'}`, type: 'weight' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weight]);

  // Log connection state changes
  useEffect(() => {
    addLog({ ts: new Date().toISOString(), message: `Connection: ${status}`, type: 'state' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Log errors
  useEffect(() => {
    if (error) {
      addLog({ ts: new Date().toISOString(), message: `Error: ${error}`, type: 'error' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const addLog = (entry: LogEntry) => {
    setLog(prev => [...prev.slice(-19), entry]); // Keep last 20 entries
    // Auto-scroll
    setTimeout(() => logRef.current?.scrollTo(0, logRef.current.scrollHeight), 50);
  };

  // Determine weight color
  const getWeightColor = () => {
    if (status !== 'connected') return 'text-gray-400';
    if (weight === 0) return 'text-gray-500';
    return 'text-green-600 dark:text-green-400';
  };

  return (
    <div className="border-t pt-6 mt-6">
      <div className="flex items-center gap-2 mb-2">
        <Activity className="w-5 h-5 text-secondary-500" />
        <h3 className="text-lg font-medium text-secondary-900 dark:text-white">{t('admin.scaleTestPanel')}</h3>
      </div>
      <p className="text-xs text-secondary-500 dark:text-gray-400 mb-4">{t('admin.scaleTestPanelDesc')}</p>

      {/* Scale selector */}
      <div className="flex items-center gap-4 mb-4">
        <select
          value={selectedKey}
          onChange={(e) => setSelectedKey(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">{t('admin.selectScale')}</option>
          {scales.map((s) => (
            <option key={s.setting_key} value={s.setting_key}>
              {s.config.nickname}
            </option>
          ))}
        </select>

        {/* Connection status */}
        {selectedKey && (
          <div className="flex items-center gap-2">
            {status === 'connected' ? (
              <Wifi className="w-4 h-4 text-green-500" />
            ) : (
              <WifiOff className="w-4 h-4 text-gray-400" />
            )}
            <span className={`text-sm ${
              status === 'connected' ? 'text-green-600' : status === 'connecting' ? 'text-yellow-600' : 'text-gray-500'
            }`}>
              {status === 'connected'
                ? t('operator.scaleConnected')
                : status === 'connecting'
                  ? t('operator.scaleConnecting')
                  : t('operator.scaleDisconnected')
              }
            </span>
            {status === 'connected' && (
              <span className={`px-2 py-0.5 text-xs rounded-full ${
                isStable
                  ? 'bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-200'
                  : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-800 dark:text-yellow-200'
              }`}>
                {isStable ? t('operator.scaleWeightStable') : t('operator.scaleWeightUnstable')}
              </span>
            )}
            {status !== 'connected' && (
              <button
                onClick={handleReconnect}
                disabled={reconnecting}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`w-3 h-3 ${reconnecting ? 'animate-spin' : ''}`} />
                {t('admin.scaleReconnect')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Read mode toggle */}
      {selectedKey && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm text-secondary-700 dark:text-gray-300">{t('admin.scaleReadMode')}:</span>
          <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden">
            <button
              onClick={() => handleModeChange('continuous')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                readMode === 'continuous'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
              }`}
            >
              {t('admin.scaleModeContinuous')}
            </button>
            <button
              onClick={() => handleModeChange('on_print')}
              className={`px-3 py-1.5 text-xs font-medium border-l border-gray-300 dark:border-gray-600 transition-colors ${
                readMode === 'on_print'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
              }`}
            >
              {t('admin.scaleModeOnPrint')}
            </button>
          </div>
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {readMode === 'continuous' ? t('admin.scaleModeContinuousDesc') : t('admin.scaleModeOnPrintDesc')}
          </span>
        </div>
      )}

      {selectedKey && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Live weight display */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 text-center">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wider">
              {t('admin.scaleLiveWeight')}
            </div>
            <div className={`text-5xl font-mono font-bold ${getWeightColor()}`}>
              {weight.toFixed(2)}
            </div>
            <div className="text-lg text-gray-500 dark:text-gray-400 mt-1">{unit}</div>
            <div className="flex justify-center gap-3 mt-4">
              <button
                onClick={tare}
                disabled={status !== 'connected'}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {t('operator.scaleTareCommand')}
              </button>
              <button
                onClick={handleGetStableWeight}
                disabled={fetchingStable || !selectedKey}
                className="px-4 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                {fetchingStable ? t('common.loading') : t('admin.scaleStableWeight')}
              </button>
            </div>
          </div>

          {/* Raw log */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('admin.scaleRawLog')}
              </span>
              <button
                onClick={() => setLog([])}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
            <div
              ref={logRef}
              className="h-48 overflow-y-auto font-mono text-xs space-y-0.5"
            >
              {log.length === 0 ? (
                <div className="text-gray-400 italic">{t('admin.scaleNoMessages')}</div>
              ) : (
                log.map((entry, i) => (
                  <div
                    key={i}
                    className={`${
                      entry.type === 'error'
                        ? 'text-red-500'
                        : entry.type === 'state'
                          ? 'text-blue-500'
                          : 'text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    <span className="text-gray-400">{new Date(entry.ts).toLocaleTimeString()}</span>{' '}
                    {entry.message}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScaleTestPanel;
